import { useAGMValidation } from "./useAGMValidation";
import { useError } from "@contexts/ErrorContext";
import { useLoading } from "@contexts/LoadingContext";
import { getAgmCalculatedChain, getAgmWithCalcChainUsingSubchain, getElongPercents, getRealChainDelta } from "@pages/JobControl/components/helperFunctions/chainDistanceCalcs";
import { validateTableData } from "@pages/JobControl/components/helperFunctions/validateTableData";
import { AGMEditSchema } from "@pages/JobControl/components/tableConfig/jobControlSchemas";
import { createAGMsAPI, deleteAGMsAPI, getAGMsAPI, updateAGMsAPI } from "@services/agmsAPI";
import { useCallback, useEffect, useState } from "react";
import { agmInitialData as initialData } from "../../helperFunctions/initalTableData";
import { useUnsavedChanges } from '@contexts/UnsavedChangesContext'
import { areObjectsEqual } from "@utils/areObjectsEqual";
import { useSuccess } from '@contexts/SuccessContext'
import { resetStateAfterDelete } from "../../helperFunctions/resetStateAfterDelete";
import { useUser } from "@contexts/UserContext";
import { fetchJobData } from "@utils/jobs/fetchJobData";
import { hasAGMLatLngChange } from "../../helperFunctions/checkAGMCoordinateChange";
import { updateJobAPI } from "@services/jobsAPI";

export const useAGMActions = (selectedJob, setSelectedJob, requestConfirm) => {
  const { 
    validateAgms,
    validationErrors,
    setValidationErrors,
    setRealChainValidationErrors,
    realChainValidationErrors
  } = useAGMValidation();
  const { showError } = useError();
  const { showLoading, hideLoading } = useLoading();
  const { setHasUnsavedChanges } = useUnsavedChanges();
  const { timeoutSuccess, confirmSuccess } = useSuccess();

  const [data, setData] = useState(initialData)
  const [originalData, setOriginalData] = useState([]) //use to compare original and changed data when time to submit
  
  const [importedJobData, setImportedJobData] = useState()

  const [subchains, setSubchains] = useState([])
  const [isUnlockedWithPOIs, setIsUnlockedWithPOIs] = useState(false)

  const {storedJob, setStoredJob } = useUser()

  useEffect(() => {
    if (!selectedJob) return
    fetchAGMs(selectedJob.value)
    setValidationErrors([])
    setRealChainValidationErrors([])

    setIsUnlockedWithPOIs(selectedJob.POICount > 0 && !selectedJob.IsLocked)

  }, [selectedJob]); //eslint-disable-line

  const fetchAGMs = async (jobId) => {
    showLoading("Getting AGMs...")

    let response = await getAGMsAPI(jobId);

    if (!response.success) {
      hideLoading()
      showError(`Error getting AGMs: ${response.message}`)
    } else {
      let agms = response.data

      //keep subchains in state in case we need them to recalculate later
      if(response.subchains.length){
        setSubchains(response.subchains)
      }

      if (agms.length > 0) {

        //verify we have real chain values for all agms
        //if we don't, sort agms by Est_Chain
        const agmsHaveRealChain = agms.every(
          agm => agm.Real_Chain !== null && agm.Real_Chain !== undefined
        );

        const sortBy = agmsHaveRealChain ? "Real_Chain" : "Est_Chain";

        //remove CreatedAt since that will never be updated in the frontend
        //formatting boolean fields to match dropdown options
        const formattedData = agms.map(({ CreatedAt, ...agm }) => {
          return {
            ...agm,
            Verified: agm.Verified === true ? "Yes" : "No",
            GPS_Taken: agm.GPS_Taken === true ? "Yes" : "No"
          }
        }).sort((a,b) => a[sortBy] - b[sortBy] ) //put the AGMs in order

        //do the calculated chain calcs here - 
        //if no subchains use agm points & haversine to calculate straight lines 
        //      ->if no subchains - give warning that user should enter subchains for more accurate distances
        let dataWithCalcChain;
        if (response.subchains.length) {
          dataWithCalcChain = getAgmWithCalcChainUsingSubchain(formattedData, response.subchains);
        } else {
          //using show error to display a message
          showError('There are no Subchains - Enter Subchain Points For A More Accurate Calculated Chain');
          dataWithCalcChain = getAgmCalculatedChain(formattedData);
        }

        //need to check that db and frontend calcs match for unsavedchanges context/hook
        const epsilon = 0.000001; //numbers can be this much off and still be equal
        for (let i = 0; i < formattedData.length; i++) {
          if (Math.abs(dataWithCalcChain[i].Calc_Chain - formattedData[i].Calc_Chain) > epsilon) {
            setHasUnsavedChanges(true);
            break;
          }
        }

        setData(dataWithCalcChain); //added by Bryan for calculated Chain
        setOriginalData(formattedData);

        hideLoading();
        return;
      }

      //if no data was returned from the fetch, reset state to initial data
      setData(initialData);
      hideLoading();
    }
  }

  //used when new row added to the table
  //for data that is already in the table, not newly fetched data
  const recalculateCalcChain = (updatedData) => {

    let dataWithCalcChain;

    //if no subchains use agm points & haversine to calculate straight lines; give user warning that subchains give more accurate distances
    if (subchains.length) {
      dataWithCalcChain = getAgmWithCalcChainUsingSubchain(updatedData,subchains);
    } else {
      //using show error to display a message
      showError('There are no Subchains - Enter Subchain Points For A More Accurate Calculated Chain');
      dataWithCalcChain = getAgmCalculatedChain(updatedData);
    }

    return dataWithCalcChain
  }

  const handleSubmit = async (setMissingColumns, setFileName) => {
    //show loading message, remove old submit message if exists
    showLoading("Submitting...");

    //validation for real chain values

    //Create a deep copy for mutation
    let agmData = data.map((agm) => ({ ...agm }));

    let validation = validateAgms(agmData); //returns an error OR object with result:false if issues
    if (validation instanceof Error) {
      showError(validation.message);
      hideLoading();
      return;
    }

    if (!validation.result) {
      const {
        validationErrors = [],
        realChainErrors = [],
        realChainDeltaErrors = []
      } = validation;

      const hasHard = validationErrors.length > 0;
      const hasWarnings = realChainErrors.length > 0 || realChainDeltaErrors.length > 0;

      // if there are no distance warnings, treat as hard failure
      if (!hasWarnings) {
        showError(validation.message || 'Please correct the highlighted values');
        hideLoading();
        return;
      }

      // construct warning message similarly to before
      let message;
      if (realChainErrors.length > 0 && realChainDeltaErrors.length > 0) {
        message =
          'Some real chain distances may be out of the expected range (see highlighted Real Chain and Real Chain Delta columns and notes below). Would you like to submit anyway?';
      } else if (realChainErrors.length > 0) {
        message =
          'Some distances between sites may be shorter than expected (see notes below AGM table and highlighted boxes). Would you like to submit anyway?';
      } else if (realChainDeltaErrors.length > 0) {
        message =
          'Some distances between sites may be too large (see highlighted Real Chain Delta values). Would you like to submit anyway?';
      } else {
        message = 'There were validation errors';
      }

      if (hasHard) {
        // add an explicit note about the additional hard validation problems
        message += ' Please correct all highlighted cells before submitting.';
      }

      const isConfirmed = await requestConfirm(message, 'Yes, submit', 'Cancel');

      if (!isConfirmed) {
        hideLoading();
        return;
      }
    }

    //get elong percent here
    agmData = getElongPercents(agmData)
    if(agmData instanceof Error){
      showError(`Error getting Elongation - ${agmData.message}`);
      hideLoading();
      return;
    }

    //check if required fields in table are filled in
    const isValid = validateTableData(agmData, setValidationErrors, AGMEditSchema);

    if (!isValid) {
      hideLoading();
      return;
    }

    //submit agms to api  
    //api expects arrays of agms, so create empty arrays to push new/updated agms into
    let newAGMs = [];
    let updatedAGMs = [];
    const originalDataMap = new Map(originalData.map(row => [row.AGMID, row]));

    for (const agm of agmData) {

      //UPDATE AGMs - AGMs that are not new (have a AGMID) and are different from the original data (to reduce api calls)
      if (agm.hasOwnProperty("AGMID")) {

        //compare to original data to see if AGM data has changed
        const originalRow = originalDataMap.get(agm.AGMID);

        if (!areObjectsEqual(agm, originalRow)) {
          updatedAGMs.push({
            ...agm,
            Verified: agm.Verified === "Yes" ? true : false,
            GPS_Taken: agm.GPS_Taken === "Yes" ? true : false
          });
        }

      } else {

        //CREATE AGMs - if no AGMID, it's a new AGM

        //format some fields to match what db is expecting (int, bool etc)
        const calc_chain = agm.Calc_Chain !== "" && agm.Calc_Chain != null ? Number(agm.Calc_Chain) : null
        const delta = agm.Real_Chain_Delta !== "" && agm.Real_Chain_Delta != null ? Number(agm.Real_Chain_Delta) : null

        const newAGM = {
          ...agm,
          JobID: selectedJob.value,
          Verified: agm.Verified === "Yes" ? true : false,
          GPS_Taken: agm.GPS_Taken === "Yes" ? true : false,
          Calc_Chain: calc_chain,
          Real_Chain_Delta: delta
        }

        newAGMs.push(newAGM);
      }

    }

    //if no changes made, no need to update state, also don't want to tell user it saved when nothing happened
    if (newAGMs.length === 0 && updatedAGMs.length === 0) {
      setHasUnsavedChanges(false);

      hideLoading();

      timeoutSuccess("No changes");

      return;
    }

    //submit updated AGMs are if there are any
    if (updatedAGMs.length > 0) {
      const response = await updateAGMsAPI(updatedAGMs);

      if (!response.success) {
        hideLoading();

        showError(`Error updating AGMs: ${response.message}`);

        return;
      }
    }

    //get all the agms that aren't new, for updating state later
    let dataToUpdate = agmData.filter(agm => agm.hasOwnProperty("AGMID"));

    //submit new AGMS are if there are any
    if (newAGMs.length > 0) {
      const response = await createAGMsAPI(newAGMs);

      if (!response.success) {
        hideLoading();

        showError(`Error creating AGMs: ${response.message}`)

        return;
      }

      //format new agms, now with AGMIDs
      const AGMsWithIDs = response.data.map(({ CreatedAt, ...agm }) => {
        return {
          ...agm,
          Verified: agm.Verified === true ? "Yes" : "No",
          GPS_Taken: agm.GPS_Taken === true ? "Yes" : "No",
        }
      });
      dataToUpdate.push(...AGMsWithIDs);
    }

    //if there is job data imported from excel, update some fields - we are saving Datum, GEOID and UTM in the database to be used in export, but not to be shown/editable in frontend
    //don't want to overwrite some existing fields at this time (like job number/name, etc)
    if(importedJobData){
      const jobDataToUpdate = {
        Datum: importedJobData.Datum,
        GEOID: importedJobData.GEOID,
        UTM: importedJobData.UTM,
        JobID: selectedJob.value
      }

      const response = await updateJobAPI(jobDataToUpdate)

      if(!response.success){
        showError(response.message)
      }
    }

    //verify we have real chain values for all agms
    //if we don't, sort agms by Est_Chain
    const agmsHaveRealChain = dataToUpdate.every(
      agm => agm.Real_Chain !== null && agm.Real_Chain !== undefined
    );

    const sortBy = agmsHaveRealChain ? "Real_Chain" : "Est_Chain";

    dataToUpdate.sort((a,b) => a[sortBy] - b[sortBy])

    //update state
    setData(dataToUpdate);
    setOriginalData(JSON.parse(JSON.stringify(dataToUpdate)));
    setValidationErrors([])
    
    setStoredJob(prev => {
      return {
        ...prev,
        AGMCount: dataToUpdate.length
      }
    })

    //if excel import had missing columns, reset message
    setMissingColumns([]);

    //remove excel file name from page
    setFileName(null);

    setHasUnsavedChanges(false);

    hideLoading();


    //if AGM(s) added to existing set of AGMs, warn that they should check their subchains
    //reason: if they add a new AGM between a subchain and its US/DS site, the US/DS site and distances are now inaccurate

    const hasExistingAGMs = originalData.length > 0;

    const wereAgmsAdded =
      hasExistingAGMs && data.length > originalData.length;

    const didAGMLatLngChange = hasAGMLatLngChange({
      originalData,
      updatedAGMs
    });

    if (hasExistingAGMs && (wereAgmsAdded || didAGMLatLngChange)) {
      const message = didAGMLatLngChange
          ? 'Saved successfully! Check subchains – AGM lat/lng changed, which may affect upstream and downstream distances'
          : subchains && subchains.length > 0 ? 'Saved successfully! Check subchains – adding AGMs may change upstream or downstream sites' : ''

      if(message.length > 0) {
        confirmSuccess(message);
      }
      
    } else {
      timeoutSuccess("Saved");

    }
  }
  const handleDeleteOneAGM = useCallback(async (rowData, rowIndex) => {
    showLoading("Deleting AGM...");

    const AGMWasInDB = Boolean(rowData.AGMID)

    //if there is no ID, doesn't exist yet in database, so don't need to call API
    //just delete from table in that case
    if (AGMWasInDB) {
      const response = await deleteAGMsAPI([rowData.AGMID]);

      if (!response.success) {
        hideLoading();

        showError(`Error deleting AGM: ${response.message}`);

        return;
      }

      //re-fetch job data (keeps front-end messaging consistent if agm/subchain/poi counts, or islocked changed)
      fetchJobData(storedJob, showError, setSelectedJob, setStoredJob)
    }

    // recalculate AGM data (once agm deleted, calc chain, delta, elong percents might change)
    const prevAGMsCopy = [...data];

    let recalculatedData;

    if (prevAGMsCopy.length <= 1) {
      recalculatedData = [...initialData];
    } else {
      prevAGMsCopy.splice(rowIndex, 1);

      recalculatedData = recalculateCalcChain(prevAGMsCopy)

      recalculatedData.forEach(agm => {
        const delta = getRealChainDelta(recalculatedData, agm);
        agm.Real_Chain_Delta = delta != null ? Number(delta) : null;
      })

      if (AGMWasInDB) {
        recalculatedData = getElongPercents(recalculatedData);

        if(recalculatedData instanceof Error){
          showError(`Error getting Elongation - ${recalculatedData.message}`);
          hideLoading();
          return;
        }
      }
    }

    // if agms exist in db (not newly imported), update db
    if (AGMWasInDB && recalculatedData.length > 0) {
      const isValid = validateTableData(recalculatedData, setValidationErrors, AGMEditSchema);

      if (!isValid) {
        hideLoading();
        showError("AGM deleted, but could not submit re-calculated values for remaining AGMs. Please see highlighted boxed and fix issues, then click Submit.");
        return;
      }

      const formattedDataForUpdate = recalculatedData.map(agm => {
        return {
          ...agm,
          Verified: agm.Verified === "Yes" ? true : false,
          GPS_Taken: agm.GPS_Taken === "Yes" ? true : false
        }
      })
      const response = await updateAGMsAPI(formattedDataForUpdate);

      if (!response.success) {
        hideLoading();
        showError(`Error updating AGMs after delete: ${response.message}`);
        return;
      }
    }

    setData(recalculatedData);

    //delete successful - remove item from data and let user know

    hideLoading();

    timeoutSuccess("Delete successful");

  }, [showLoading, deleteAGMsAPI, hideLoading, resetStateAfterDelete, initialData, setData, recalculateCalcChain]); //eslint-disable-line


  const handleDeleteAllAGMs = async (setMissingColumns, setFileName) => {
    // user confirmed
    showLoading("Deleting AGMs...");

    //if agm has id, it exists in database, so need to delete it from there
    const deleteAGMsFromDB = data
      .filter(agm => agm.AGMID !== undefined)
      .map(agm => agm.AGMID);

    if (deleteAGMsFromDB.length > 0) {
      const response = await deleteAGMsAPI(deleteAGMsFromDB);

      if (!response.success) {
        hideLoading();

        showError(`Error deleting AGMs: ${response.message}`);

        return;
      }
    }

    //everything submitted successfully, so let user know and reset table

    setData(initialData);

    //if excel import had missing columns, reset message
    setMissingColumns([]);

    //remove excel file name from page
    setFileName(null);

    hideLoading();

    setHasUnsavedChanges(false);

    timeoutSuccess("Delete successful");

    //re-fetch job data (keeps front-end messaging consistent if agm/subchain/poi counts, or islocked changed)
    fetchJobData(storedJob, showError, setSelectedJob, setStoredJob)
  }

  const submitAdjustRealChainClick = (selectedName) => {
    let newData = data.map(agm => ({ ...agm }));
    newData.forEach((agm) => {
      if (selectedName === 'Est_Chain') {
        agm.Real_Chain = Number(agm.Est_Chain);
      }
      if (selectedName === 'Calc_Chain') {
        agm.Real_Chain = Number(agm.Calc_Chain);
      }

      const delta = getRealChainDelta(newData, agm);
      agm.Real_Chain_Delta = delta != null ? Number(delta) : null;
      
    });

    setData(newData);

    //check to see if data has changed
    for (let i = 0; i < newData.length; i++) {
      if (originalData.length === 0 || newData[i].Real_Chain !== originalData[i].Real_Chain) {
        setHasUnsavedChanges(true);
        break;
      }
    }
  }



  //for AGMEdit - need to recalculate calc_chain when new row added
  const handleAddRowAGMEdit = (insertIndex, newRowData) => {
      //create row object, creating a cell for each item in the schema
      let newRow = {}
      
      Object.keys(AGMEditSchema).forEach(key => {
          newRow[key] = ""
      })

      //replace empty fields with the data that the user entered
      if(newRowData){
          newRow = {
              ...newRow,
              ...newRowData
          }
      }

      //copy the old data, and insert the new row where the user specified
      //must be in order to determine correct calc_chain for each agm
      const newData = structuredClone(data)

      if(insertIndex !== undefined && insertIndex !== null){
          newData.splice(insertIndex, 0, newRow)
      } else {
          newData.push(newRow)
      }

      //recalculate calc_chain for each agm, as well as delta
      const recalculatedWithCalcChain = recalculateCalcChain(newData)

      recalculatedWithCalcChain.forEach(agm => {
          const delta = getRealChainDelta(recalculatedWithCalcChain, agm);
          agm.Real_Chain_Delta = delta != null ? Number(delta) : null;
      })

      setData(recalculatedWithCalcChain)
      
      setHasUnsavedChanges(true)

      //give user a message reminding them to adjust the real chain values now that new row is included

        confirmSuccess(
          <span>Next, click 
              <strong className="mx-2">
                <i className="fa-solid fa-exchange text-purple-800 mr-2"></i>
                Adjust Real Chain
              </strong> 
              to calculate Real Chain and Real Chain Delta values for the new row
          </span>
        )
  }

    //add in calc chain after AGMs imported from excel
    const handleProcessExcelData = (agmData, jobData) => {

        const dataWithCalcChain = recalculateCalcChain(agmData)

        setData(dataWithCalcChain)
        setOriginalData(JSON.parse(JSON.stringify(dataWithCalcChain)))
        setImportedJobData(jobData)

        //compare job data from spreadsheet with selectedJob, warn if these are different
        const fieldsToCompare = ['Job_Number', 'Job_Name', 'Client', 'Thickness', 'Diameter', 'Line_Name']
        const { different, missingInSelectedJob } = getDifferingJobFields(fieldsToCompare, jobData, selectedJob)

        if (different.length > 0 || missingInSelectedJob.length > 0) {
            showError(
                <div className='text-left'>
                    {different.length > 0 && (
                        <div className='mb-6'>
                            <p className='mb-2'>
                                <i className="fa-solid fa-circle-exclamation mr-2 text-amber-600"></i>
                                These job fields from the Excel sheet are different from what is already in Job Details:
                            </p>
                            <ul className='ml-6'>
                                {different.map(item => (
                                    <li key={item.field}>
                                        <strong>{item.field?.replace("_", " ")}</strong>: Excel = {item.currentValue}, Job Details = {item.originalValue}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {missingInSelectedJob.length > 0 && (
                        <div>
                            <p className='mb-2'>
                                <i className="fa-solid fa-circle-exclamation mr-2 text-amber-600"></i>
                                These fields are filled in the Excel sheet but missing in Job Details:
                            </p>
                            <ul className='ml-6'>
                                {missingInSelectedJob.map(item => (
                                    <li key={item.field}>
                                        <strong>{item.field?.replace("_", " ")}</strong>: {item.currentValue}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            );
        }


        //check if they have real chain values - if they are missing, show message to click adjust real chain
        const agmsMissingRealChain = agmData.filter(
            agm => agm.Real_Chain === null || agm.Real_Chain === undefined
        );

        //only launch should have a value of 0
        const agmsWithZeroRealChain = agmData.filter(agm => agm.Real_Chain === 0)

        if (agmsMissingRealChain.length > 0 || agmsWithZeroRealChain.length > 1) { 
            confirmSuccess(
                <span>Ensure all AGMs have a Real Chain value. You can click 
                    <strong className="mx-2">
                        <i className="fa-solid fa-exchange text-purple-800 mr-2"></i>
                        Adjust Real Chain
                    </strong> 
                    to calculate Real Chain and Real Chain Delta values for the table.
                </span>
            )
        }

    }

    //helpers for showing job details diff message
    const normalize = (value) => {
        if (value === null || value === undefined) return '';
        // Convert numeric strings to numbers
        if (!isNaN(value) && value !== '') {
            return Number(value);
        }
        return String(value).trim();
    }

    const getDifferingJobFields = (jobFields, currentValues, selectedJob) => {
        const different = [];
        const missingInSelectedJob = [];

        jobFields.forEach(field => {
            const currentValue = normalize(currentValues[field]);
            const originalValue = normalize(selectedJob?.[field]);

            // Ignore empty Excel values entirely
            if (!currentValue) return;

            if (!originalValue) {
                // Excel has value, job is missing it
                missingInSelectedJob.push({ field, currentValue });
            } else if (currentValue !== originalValue) {    
                // Both have values, but they differ
                different.push({ field, currentValue, originalValue });
            }
        });

        return { different, missingInSelectedJob };
    };


  return { 
    handleSubmit,
    data,
    setData,
    setImportedJobData,
    originalData,
    setOriginalData,
    handleDeleteAllAGMs,
    handleDeleteOneAGM,
    submitAdjustRealChainClick,
    validationErrors,//pass from useAGMValidation
    setValidationErrors,//pass from useAGMValidation
    realChainValidationErrors, //pass from useAGMValidation
    handleAddRowAGMEdit,
    recalculateCalcChain,
    isUnlockedWithPOIs,
    handleProcessExcelData
  };
};
